import { useCallback, useMemo, useRef, useState } from 'react';
import { runRules, type Rule } from './rules';
import { applyGuard, type GuardMode } from './sanitize';

/**
 * Form state + validation for the panel and web screens.
 *
 * <p>Deliberately small and dependency-free: the repo has no form library and adding one would
 * mean rewriting 54 screens' worth of already-working state handling. This hook wraps the shape
 * those screens already use — a values object and a `set(patch)` — and adds errors, touched
 * tracking and submit gating around it, so a screen migrates by changing how it reads state, not
 * how it is built.</p>
 *
 * <p><b>When errors appear.</b> A field validates on every change, but its message is only shown
 * once the user has left it (blur) or has tried to submit. Validating on change while hiding the
 * message until blur is what makes an error disappear the instant it is fixed, without shouting at
 * someone who is still halfway through typing their email address.</p>
 */

export type Schema<F extends Record<string, unknown>> = {
  [K in keyof F]?: readonly Rule<F[K], F>[];
};

/** Per-field input shaping, applied on change (and therefore on paste). */
export type Guards<F extends Record<string, unknown>> = {
  [K in keyof F]?: { mode?: GuardMode; maxLength?: number };
};

export interface UseValidatedFormOptions<F extends Record<string, unknown>> {
  schema: Schema<F>;
  guards?: Guards<F>;
  /** Rules that depend on other fields (confirm password, To > From) re-run every field on change. */
  crossField?: boolean;
}

export interface ValidatedForm<F extends Record<string, unknown>> {
  values: F;
  errors: Partial<Record<keyof F, string>>;
  touched: Partial<Record<keyof F, boolean>>;
  /** True only when every rule passes — bind this to the Save button's `disabled`. */
  isValid: boolean;
  /** True once submit has been attempted; makes every error visible. */
  submitted: boolean;
  /** The error to display for a field: present only once touched or submitted. */
  errorFor: (key: keyof F) => string | undefined;
  setValue: <K extends keyof F>(key: K, value: F[K]) => void;
  /** Patch several fields at once — the shape the existing screens already use. */
  set: (patch: Partial<F>) => void;
  /** Replace all values (loading a record for edit) and reset touched/errors. */
  reset: (next: F) => void;
  setTouched: (key: keyof F) => void;
  /** Validate everything and return whether it passed. Marks the form submitted. */
  validateAll: () => boolean;
  /** Wrap a save handler: runs validation first and only calls through when the form is valid. */
  handleSubmit: (onValid: (values: F) => void | Promise<void>) => (e?: { preventDefault?: () => void }) => void;
  /** Props for a text input: value, onChange (guarded), onBlur, and the a11y error wiring. */
  bind: <K extends keyof F>(key: K) => {
    value: F[K];
    onChange: (e: { target: { value: string } }) => void;
    onBlur: () => void;
    'aria-invalid': true | undefined;
    error: string | undefined;
  };
}

/**
 * Run rules against one value. The building block for a screen that computes its own error map.
 *
 * `check(p.name, v.required('Party name'), v.maxLength(100, 'Party name'))`
 */
export function check<T>(value: T, ...rules: readonly Rule<T, Record<string, unknown>>[]): string | null {
  return runRules(value, {} as Record<string, unknown>, rules);
}

/**
 * Touched/submitted tracking over an error map the caller computes.
 *
 * <p>The panel screens hold deeply-shaped state — a party has `billing.pin` and `shipping.pin`,
 * an invoice has a line array — which a flat values-object hook can't address without rewriting
 * how the screen stores its data. This inverts the relationship instead: the screen computes
 * `{ field: message | null }` however it likes (with `check`), and this supplies the only part
 * that genuinely needs to be stateful — whether a given field has been touched yet.</p>
 *
 * <p>Because the error map is recomputed on every render from live state, validation is real-time
 * by construction: there is no cached error to go stale.</p>
 */
export function useValidator<K extends string>(problems: Partial<Record<K, string | null | undefined>>): {
  /** True when no field has a problem — bind to the Save button's `disabled`. */
  isValid: boolean;
  /** The message to display: present once the field is touched or submit has been attempted. */
  error: (key: K) => string | undefined;
  /** `onBlur` handler for a field. */
  blur: (key: K) => () => void;
  /** Reveal every message (call at the top of save), and report whether the form is valid. */
  markAll: () => boolean;
  /** Clear touched + submitted, e.g. after loading a record for edit. */
  reset: () => void;
  submitted: boolean;
  /** The first message, for a screen that also wants to toast a summary. */
  firstProblem: string | undefined;
} {
  const [touched, setTouched] = useState<Partial<Record<K, boolean>>>({});
  const [submitted, setSubmitted] = useState(false);

  const entries = Object.entries(problems) as [K, string | null | undefined][];
  const firstProblem = entries.find(([, message]) => Boolean(message))?.[1] ?? undefined;
  const isValid = !firstProblem;

  const blur = useCallback(
    (key: K) => () => setTouched((prev) => (prev[key] ? prev : { ...prev, [key]: true })),
    []
  );

  return {
    isValid,
    error: (key: K) => (submitted || touched[key] ? problems[key] ?? undefined : undefined),
    blur,
    markAll: () => {
      setSubmitted(true);
      return isValid;
    },
    reset: () => {
      setTouched({});
      setSubmitted(false);
    },
    submitted,
    firstProblem
  };
}

/**
 * Validation for a single field that keeps its own `useState`.
 *
 * <p>Many screens here have one or two inputs wrapped in bespoke chrome — the OTP boxes, the
 * "+91" prefix, a search box inside a toolbar. Rebuilding those around a form object would mean
 * rewriting layout to gain nothing, so this attaches the same rules to state that already exists:
 * the value stays where it is, and the hook supplies the error and the blur handler.</p>
 *
 * <p>`error` follows the same show-on-blur rule as the form hook; `problem` is the unfiltered
 * result, for gating a submit button that must be disabled before the field is ever touched.</p>
 */
export function useField<T>(
  value: T,
  rules: readonly Rule<T, Record<string, unknown>>[],
  opts: { show?: boolean } = {}
): {
  error: string | undefined;
  problem: string | undefined;
  isValid: boolean;
  touched: boolean;
  onBlur: () => void;
  markTouched: () => void;
  resetTouched: () => void;
} {
  const [touched, setTouched] = useState(false);
  const problem = runRules(value, {} as Record<string, unknown>, rules) ?? undefined;
  const show = opts.show ?? touched;
  return {
    error: show ? problem : undefined,
    problem,
    isValid: !problem,
    touched,
    onBlur: () => setTouched(true),
    markTouched: () => setTouched(true),
    resetTouched: () => setTouched(false)
  };
}

export function useValidatedForm<F extends Record<string, unknown>>(
  initial: F,
  options: UseValidatedFormOptions<F>
): ValidatedForm<F> {
  const { schema, guards } = options;
  const [values, setValues] = useState<F>(initial);
  const [touched, setTouchedState] = useState<Partial<Record<keyof F, boolean>>>({});
  const [submitted, setSubmitted] = useState(false);

  // The schema is rebuilt on every render by callers that inline it, so hold it in a ref to keep
  // the memo below from thrashing while still reading the latest rules.
  const schemaRef = useRef(schema);
  schemaRef.current = schema;

  /** Every field's error, recomputed from the current values — the single source of truth for
   *  both display and the submit gate, so the button can never disagree with the fields. */
  const errors = useMemo(() => {
    const out: Partial<Record<keyof F, string>> = {};
    for (const key of Object.keys(schemaRef.current) as (keyof F)[]) {
      const message = runRules(values[key], values, schemaRef.current[key]);
      if (message) out[key] = message;
    }
    return out;
  }, [values]);

  const isValid = Object.keys(errors).length === 0;

  const setValue = useCallback(
    <K extends keyof F>(key: K, value: F[K]) => {
      setValues((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  const set = useCallback((patch: Partial<F>) => {
    setValues((prev) => ({ ...prev, ...patch }));
  }, []);

  const reset = useCallback((next: F) => {
    setValues(next);
    setTouchedState({});
    setSubmitted(false);
  }, []);

  const setTouched = useCallback((key: keyof F) => {
    setTouchedState((prev) => (prev[key] ? prev : { ...prev, [key]: true }));
  }, []);

  const errorFor = useCallback(
    (key: keyof F) => (submitted || touched[key] ? errors[key] : undefined),
    [errors, touched, submitted]
  );

  const validateAll = useCallback(() => {
    setSubmitted(true);
    // Recompute against the live values rather than trusting the memo, which may not have
    // flushed if submit was triggered in the same tick as a change.
    for (const key of Object.keys(schemaRef.current) as (keyof F)[]) {
      if (runRules(values[key], values, schemaRef.current[key])) return false;
    }
    return true;
  }, [values]);

  const handleSubmit = useCallback(
    (onValid: (values: F) => void | Promise<void>) => (e?: { preventDefault?: () => void }) => {
      e?.preventDefault?.();
      if (!validateAll()) return;
      void onValid(values);
    },
    [validateAll, values]
  );

  const bind = useCallback(
    <K extends keyof F>(key: K) => {
      const guard = guards?.[key];
      return {
        value: values[key],
        onChange: (e: { target: { value: string } }) => {
          const raw = e.target.value;
          const next = guard ? applyGuard(raw, guard.mode, guard.maxLength) : raw;
          setValues((prev) => ({ ...prev, [key]: next as F[K] }));
        },
        onBlur: () => setTouched(key),
        'aria-invalid': (submitted || touched[key]) && errors[key] ? (true as const) : undefined,
        error: submitted || touched[key] ? errors[key] : undefined
      };
    },
    [values, guards, errors, touched, submitted, setTouched]
  );

  return {
    values,
    errors,
    touched,
    isValid,
    submitted,
    errorFor,
    setValue,
    set,
    reset,
    setTouched,
    validateAll,
    handleSubmit,
    bind
  };
}
